import { useInfiniteQuery } from '@tanstack/react-query';
import { getV1MessagingChannels, MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { MessagingChannel } from '~hooks/useMessaging/models';
import useReportRequestError from '~hooks/useReportRequestError';
import { messagingChannelsQueryKey } from '~queries/messagingQueryKeys';
import { extractPagination } from '~services/pagination';

export type MessagingChannelsPage = {
  channels: MessagingChannel[];
  nextCursor: string | undefined;
};

export default function useMessagingChannelsInfiniteQuery({
  archived,
  channelType,
  hasUnread,
  searchQuery,
}: {
  archived?: boolean;
  channelType?: MessagingChannelType;
  hasUnread?: boolean;
  searchQuery?: string;
}) {
  const query = useInfiniteQuery({
    queryKey: messagingChannelsQueryKey({
      archived,
      channelType,
      hasUnread,
      searchQuery,
    }),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }): Promise<MessagingChannelsPage> => {
      const response = await getV1MessagingChannels({
        query: {
          'page[limit]': DEFAULT_API_PAGE_SIZE,
          ...(pageParam ? { 'page[after]': pageParam } : {}),
          ...(archived !== undefined ? { 'filter[archived]': archived } : {}),
          ...(channelType ? { 'filter[channel_types]': [channelType] } : {}),
          // `false` would keep only read conversations, so the unfiltered list sends nothing.
          ...(hasUnread ? { 'filter[has_unread_messages]': true } : {}),
          ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
        },
        signal,
      });

      return {
        channels: response.data.data.map(MessagingChannel.parse),
        nextCursor: extractPagination(response).after,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // The list offers its own Retry, so a backoff would only hold the spinner up.
    retry: false,
  });

  // Only a failed first page has an inline Retry; later failures keep the loaded rows on screen.
  useReportRequestError({
    error: query.error,
    errorUpdatedAt: query.errorUpdatedAt,
    message: t('error_messages.messaging.failed_to_fetch_channels'),
    shouldReport: query.isRefetchError || query.isFetchNextPageError,
  });

  const channels = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.channels),
    [query.data?.pages],
  );

  return {
    channels,
    fetchMore: query.fetchNextPage,
    hasLoadFailed: query.isLoadingError,
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    retryLoad: query.refetch,
  };
}
